import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DropDown } from '@classity-edtech/ui-base/dist/components/Inputs/DropDown/DropDown';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Help } from '@mui/icons-material';
import { useState } from 'react';

export interface GroupPrice {
  groupSize: number;
  label: string;
  pricePerSession: number;
}

export interface PricingPlan {
  id: string;
  label: string;
  billingCycle: 'monthly' | 'quarterly';
  basePrice: number;
  sessionsPerMonth: number;
  subjects: string[];
  groupPrices: GroupPrice[];
  medianPricePerSession: number;
  classityDiscountPercent: number;
}

export interface PricingData {
  plans: PricingPlan[];
}

export interface EarningsCalculatorProps {
  className?: string;
  pricing: PricingData;
  /** Which plan id to use for the calculation. Defaults to first plan. */
  planId?: string;
}

const formatCurrency = (amount: number) => `₦${amount.toLocaleString('en-NG')}`;

export const EarningsCalculator = ({ className, pricing, planId }: EarningsCalculatorProps) => {
  const plan = (planId ? pricing.plans.find(p => p.id === planId) : null) ?? pricing.plans[0];

  const groupOptions = plan.groupPrices.map(g => ({
    value: g.groupSize,
    name: g.groupSize === 1 ? '1-on-1 Session' : `Group Class of ${g.groupSize}`,
  }));

  const sessionOptions = Array.from({ length: 4 }, (_, i) => ({
    value: i + 1,
    name: `${i + 1} Session${i > 0 ? 's' : ''} in a Day`,
  }));

  const [groupSize, setGroupSize] = useState<number>(plan.groupPrices[2]?.groupSize ?? 1);
  const [sessionsPerDay, setSessionsPerDay] = useState<number>(2);

  const selectedGroup = plan.groupPrices.find(g => g.groupSize === groupSize);
  const pricePerSession = selectedGroup?.pricePerSession ?? plan.medianPricePerSession;

  // Tutor earns: pricePerSession × groupSize × sessionsPerDay × sessionsPerMonth
  // minus Classity's platform cut
  const grossMonthly = pricePerSession * groupSize * sessionsPerDay * plan.sessionsPerMonth;
  const tutorShare = 1 - plan.classityDiscountPercent / 100;
  const monthlyEarnings = Math.round(grossMonthly * tutorShare);

  const groupLabel = groupOptions.find(o => o.value === groupSize)?.name ?? '';
  const sessionLabel = sessionOptions.find(o => o.value === sessionsPerDay)?.name ?? '';

  return (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Instruction row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography
          variant='body1'
          sx={{ color: globalColours['$global-colour-content-primary'], whiteSpace: 'nowrap' }}
        >
          Calculate your average monthly earnings
        </Typography>
        <Help
          sx={{
            fontSize: '16px',
            color: globalColours['$global-colour-interactive-primary-default'],
            cursor: 'pointer',
          }}
        />
      </Box>

      {/* Form row — two dropdowns */}
      <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
        {/* Group size dropdown */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DropDown
            label={groupLabel}
            value={groupSize}
            options={[{ values: groupOptions }]}
            showOptionSearch={false}
            onSelectOption={val => setGroupSize(Number(val))}
          />
        </Box>

        {/* Sessions per day dropdown */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DropDown
            label={sessionLabel}
            value={sessionsPerDay}
            options={[{ values: sessionOptions }]}
            showOptionSearch={false}
            onSelectOption={val => setSessionsPerDay(Number(val))}
          />
        </Box>
      </Box>

      {/* Result */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant='subtitle2'
          sx={{
            color: globalColours['$global-colour-content-primary'],
            letterSpacing: '-2px',
            lineHeight: '41px',
            fontSize: '37px',
          }}
        >
          {formatCurrency(monthlyEarnings)}
        </Typography>
      </Box>
    </Box>
  );
};

EarningsCalculator.propTypes = {
  className: PropTypes.string,
  pricing: PropTypes.object.isRequired,
  planId: PropTypes.string,
};

EarningsCalculator.defaultProps = {
  className: null,
  planId: null,
};

export default styled<any>(EarningsCalculator)(() => ``);
